import { useEffect, useState } from "react";
import { AlertTriangle, Clock, CheckCircle2, Eye } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { api } from "../../lib/api";

const STATUS_STYLES = {
  open:         { color: "text-[#f59e0b]",  border: "border-[#f59e0b]/40",  label: "Em aberto" },
  acknowledged: { color: "text-[#3b82f6]",  border: "border-[#3b82f6]/40",  label: "Recebido" },
  resolved:     { color: "text-[#10b981]",  border: "border-[#10b981]/40",  label: "Resolvido" },
};

const CATEGORY_LABEL = {
  damage: "Danos no alojamento",
  consumables: "Falta de consumíveis",
  lightbulb: "Lâmpadas fundidas",
  maintenance: "Manutenção",
  extra_cleaning: "Limpeza extra necessária",
  other: "Outro",
};

/**
 * EmployeeIssues — read-only list of problems the employee has reported.
 * Admin-side handling is separate; this just shows the status updates.
 */
export default function EmployeeIssues() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/employee/issues")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = items.reduce((acc, it) => {
    const key = it.status;
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-3">Suporte</div>
        <h1 className="font-display text-5xl">Problemas reportados.</h1>
        <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
          Os problemas enviados ao admin durante as suas limpezas aparecem aqui com o estado atualizado.
        </div>
      </div>

      {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}

      {!loading && items.length === 0 && (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]" data-testid="emp-issues-empty">
          <AlertTriangle className="mx-auto text-[#d4af37] mb-3" size={28} />
          Ainda não reportou problemas. Abra uma tarefa em curso para reportar.
        </div>
      )}

      {["open", "acknowledged", "resolved"].map(s => {
        const list = grouped[s] || [];
        if (list.length === 0) return null;
        const style = STATUS_STYLES[s];
        return (
          <section key={s} data-testid={`emp-issues-${s}`}>
            <div className={`eyebrow mb-3 flex items-center gap-2 ${style.color}`}>
              {s === "resolved" ? <CheckCircle2 size={13} />
                : s === "acknowledged" ? <Eye size={13} />
                : <Clock size={13} />}
              {style.label} · <span className="text-[#a0b2af] normal-case tracking-normal">{list.length}</span>
            </div>
            <div className="space-y-2">
              {list.map(it => (
                <div key={it.id} className={`lux-card p-4 border-l-4 ${style.border}`} data-testid={`emp-issue-${it.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#d4af37] uppercase tracking-widest">
                        {CATEGORY_LABEL[it.category] || it.category}
                      </div>
                      <div className="text-sm mt-1">{it.description}</div>
                      <div className="text-[10px] text-[#a0b2af] mt-2">
                        {format(parseISO(it.created_at), "d 'de' MMM yyyy 'às' HH:mm", { locale: pt })}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${style.border} ${style.color}`}>
                      {style.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
